/**
 * Shared artifact download helpers used by every per-kind canvas.
 *
 * Centralizes the PDF download flow so the document, html, and presentation
 * canvases speak the same wire format and produce filenames consistently.
 *
 * @module lib/artifacts/download
 */

import { toast } from "sonner";
import { getAuthHeader } from "@/utils/auth-storage";

/**
 * Strips characters that are unsafe in cross-platform filenames so the saved
 * download name is always valid.
 */
export function safeArtifactFilename(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return slug || "artifact";
}

/**
 * Shared blob-streaming download — both PDF and DOCX use the same path,
 * differing only in URL suffix and saved filename extension. Centralizing
 * keeps auth, error handling, and `Object URL` cleanup identical.
 */
async function downloadArtifactBlob(input: {
  id: string;
  title: string;
  /** Server URL suffix after `/artifacts/:id/`. */
  pathSuffix: "download.pdf" | "download.docx";
  /** Saved-file extension on the user's disk. */
  extension: "pdf" | "docx";
  /** Human label used in the failure toast and console error. */
  label: string;
}): Promise<void> {
  if (!input.id) {
    toast.error("This artifact is not ready to download yet.");
    return;
  }
  try {
    const baseUrl =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api";
    const authHeader = getAuthHeader();
    const response = await fetch(
      `${baseUrl.replace(/\/$/, "")}/artifacts/${input.id}/${input.pathSuffix}`,
      { headers: authHeader ? { Authorization: authHeader } : undefined },
    );
    if (!response.ok) {
      // Try to surface the server's error body — DOCX rejects non-document
      // kinds with a 400 + structured message we want the user to see.
      let detail = `${response.status} ${response.statusText}`;
      try {
        const body = (await response.json()) as { message?: string };
        if (body?.message) detail = body.message;
      } catch {
        /* not JSON; fall back to status text */
      }
      throw new Error(`${input.label} download failed: ${detail}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeArtifactFilename(input.title)}.${input.extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(
      err instanceof Error ? err.message : "Failed to download artifact",
    );
  }
}

/**
 * Downloads the current artifact body as a server-rendered PDF.
 *
 * Uses `fetch` directly (rather than the JSON-typed `api` helper) because
 * the response is a binary blob — the api-client always parses as JSON.
 * The Authorization header is pulled from the same storage the api-client
 * uses so cookieless local dev sessions still authenticate.
 */
export async function downloadArtifactPdf(input: {
  id: string;
  title: string;
}): Promise<void> {
  await downloadArtifactBlob({
    ...input,
    pathSuffix: "download.pdf",
    extension: "pdf",
    label: "PDF",
  });
}

/**
 * Downloads a document artifact as a server-rendered Microsoft Word
 * (`.docx`) file. Only `document` kind is supported on the server — for
 * `html` / `presentation` kinds the server returns 400 and the toast
 * surfaces the reason so users know to use PDF instead.
 */
export async function downloadArtifactDocx(input: {
  id: string;
  title: string;
}): Promise<void> {
  await downloadArtifactBlob({
    ...input,
    pathSuffix: "download.docx",
    extension: "docx",
    label: "DOCX",
  });
}
